/* eslint-disable react/forbid-prop-types */
import React, { useCallback, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { createGlobalStyle, withTheme } from 'styled-components';
import Layout from './layout';
import { history } from './redux/configureStore';
import initApp from './initApp';
import { resizeListener } from './utils';
import HealthCheck from './healthCheck';
import { getParams } from './util';
import { mockInactiveSessionResponse } from './mockResponse';
import { getNextQuestionSet, getMockQuestionSet } from './redux/actions';
// import SessionStorageServices from './util/sessionStorageServices';
import ErrorBoundary from './ErrorBoundary';
import useDocumentTitle from './hooks/useDocumentTitle';

const GlobalStyle = createGlobalStyle`
	#root {
		font-family: ${({ globalFontFamily }) => globalFontFamily || 'Avenir-Book'};
		${({ globalFontColor }) => globalFontColor && `color: ${globalFontColor};`}
	}
  .content {
    padding: 16px;
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }
	// Color contrast configs for SelectOption
	@media (forced-colors: active) {
		body {
			div[class$="menu"] > div > .app-select-option {
				background-color: #000;
				color: WindowText;
				forced-color-adjust: none;
				&[data-selected="true"] {
					background-color: WindowText;
					color: Highlight;
					&:hover {
						color: ButtonText;
					}
				}
				&:hover {
					background-color: Highlight;
					color: WindowText;
				}
			}
		}
	}
`;

const Main = (props) => {
  const {
    state,
    dispatch,
    identity,
    location,
    handleMockQuestionSet,
    handleNextQuestionSet,
    theme: { config, ssoAuthConfig } = {},
  } = props;

  const store = {
    getState: () => state,
    dispatch,
  };

  const { pathname } = location;
  const initPageSkipRoutes = [
    '/ssodone',
    '/ssodone/',
    '/authError',
    '/authError/',
    '/signError',
    '/signError/',
  ];
  const screenViewport = get(state, 'meta.screenViewport');

  const _resizeListener = useCallback(resizeListener.bind(window, store), [screenViewport]);

  useEffect(() => {
    window.addEventListener('resize', _resizeListener);
    return () => {
      window.removeEventListener('resize', _resizeListener);
    };
  }, [screenViewport]);

  useEffect(() => {
    if (!initPageSkipRoutes.includes(pathname)) {
      initApp(store, config, ssoAuthConfig, identity);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const url = new URLSearchParams();

    params.forEach((value, key) => {
      if (!url.has(key)) {
        url.set(key, value);
      }
    });

    const queryString = url.toString();

    if (queryString) {
      window.history.replaceState({}, '', `?${queryString}`);
    }
  }, []);

  useEffect(() => {
    return history.listen(() => {
      const uid = getParams('uid');
      const historyActions = ['PUSH', 'POP', 'REPLACE'];
      // const identity = SessionStorageServices.getIdentity(uid);
      if (historyActions.indexOf(history.action) > -1) {
        if (identity === 'invalid') {
          handleMockQuestionSet();
          return;
        }
      }
      if (config.theme.global.disableBrowserBackButton && history.action === 'PUSH') {
        window.onpopstate = () => {
          window.history.forward();
        };
      }
      if (history.action === 'POP') {
        if (uid === null) {
          // this covers the case when user clicks on browser back button
          // at the landing page.
          // https://sureify.atlassian.net/browse/NAT-1573
          window.history.back();
        } else if (!config.theme.global.disableBrowserBackButton) {
          handleNextQuestionSet();
        }
      }
    });
  }, [history]);

  const documentTitle = get(config, 'global.appTitle', 'Navy Mutual');
  useDocumentTitle(documentTitle);

  return (
    <HealthCheck>
      <ErrorBoundary>
        <GlobalStyle
          globalFontFamily={ config.theme.global.fontFamily }
          globalFontColor={ config.theme.global.fontColor }
        />
        <Layout />
      </ErrorBoundary>
    </HealthCheck>
  );
};

const mapStateToProps = (state) => ({ state });

const mapDispatchToProps = (dispatch) => {
  const uid = getParams('uid');
  return {
    dispatch,
    handleMockQuestionSet: () => dispatch(getMockQuestionSet(mockInactiveSessionResponse)),
    handleNextQuestionSet: () =>
      dispatch(getNextQuestionSet('/questions', { uid }, { reload: true })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTheme(Main)));
